import { useCallback, useEffect } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { CurrencyContextResponse } from '@/lib/api/billing';
import { useCurrencyStore } from '@/stores/currency-store';
import { useWorkspaceStore } from '@/stores/workspace-store';

/**
 * Shared currency context, backed by a module-global Zustand store so all
 * billing/cloud surfaces share one fetch and one source of truth. Keyed by the
 * active workspace (refetches on workspace switch).
 */
export function useCurrencyContext(): {
  context: CurrencyContextResponse;
  loading: boolean;
  setPreferredCurrency: (currency: string) => Promise<void>;
  /** Force a refetch (e.g. after a top-up pins the currency → selector locks). */
  refresh: () => Promise<void>;
} {
  const { getIdToken } = useAuth();
  const activeWorkspaceId = useWorkspaceStore((s) => s.activeWorkspaceId);
  const context = useCurrencyStore((s) => s.context);
  const loading = useCurrencyStore((s) => s.loading);
  const ensureLoaded = useCurrencyStore((s) => s.ensureLoaded);
  const storeSetPreferred = useCurrencyStore((s) => s.setPreferredCurrency);
  const storeRefresh = useCurrencyStore((s) => s.refresh);

  useEffect(() => {
    void ensureLoaded(getIdToken, activeWorkspaceId);
  }, [ensureLoaded, getIdToken, activeWorkspaceId]);

  const setPreferredCurrency = useCallback(
    (currency: string) => storeSetPreferred(getIdToken, currency),
    [storeSetPreferred, getIdToken],
  );

  const refresh = useCallback(() => storeRefresh(getIdToken), [storeRefresh, getIdToken]);

  return { context, loading, setPreferredCurrency, refresh };
}
